import { STEM_NAMES } from "./stemTask";

export const newStemMix = () => Object.fromEntries(STEM_NAMES.map(name => [name, { db: 0, muted: false, solo: false }]));
export const dbToGain = db => db <= -60 ? 0 : Math.min(1, 10 ** (db / 20));
export const formatDb = db => db <= -60 ? "-inf dB" : `${db} dB`;
export const formatStemTime = seconds => `${Math.floor((seconds || 0) / 60)}:${String(Math.floor((seconds || 0) % 60)).padStart(2, "0")}`;

export function stemGain(name, mix, masterDb, temporarySolo) {
  const settings = mix[name];
  const anySolo = STEM_NAMES.some(key => mix[key].solo);
  const audible = temporarySolo ? temporarySolo === name : !settings.muted && (!anySolo || settings.solo);
  return audible ? dbToGain(settings.db) * dbToGain(masterDb) : 0;
}

// One native-media clock, with followers corrected only when drift exceeds 45 ms.
export function createStemTransport({ urls, duration = 0, onChange, onBeforePlay = () => {}, audioFactory = () => new Audio() }) {
  let audios = [];
  let mix = newStemMix();
  let masterDb = 0;
  let wanted = false;
  let starting = false;
  let disposed = false;
  let generation = 0;
  let timer;
  const listeners = [];
  let state = { time: 0, playing: false, buffering: false, temporarySolo: null, error: "" };
  const emit = patch => { state = { ...state, ...patch }; if (!disposed) onChange({ ...state }); };
  const pauseAudio = () => audios.forEach(audio => audio.pause());
  const applyMix = () => audios.forEach((audio, index) => { audio.volume = stemGain(STEM_NAMES[index], mix, masterDb, state.temporarySolo); });
  const align = time => audios.forEach(audio => {
    try { audio.currentTime = time; } catch (_) { /* Retry once metadata becomes available. */ }
  });
  const pause = () => {
    const time = state.playing && audios.length ? audios[0].currentTime : state.time;
    wanted = false; starting = false; generation += 1;
    clearInterval(timer); pauseAudio();
    emit({ time, playing: false, buffering: false, temporarySolo: null }); applyMix();
  };
  const fail = () => { pause(); emit({ error: "No se pudo reproducir. Reintenta o vuelve a abrir el analisis para renovar el acceso al audio." }); };
  const tick = () => {
    if (!state.playing || !audios.length) return;
    const time = audios[0].currentTime;
    audios.slice(1).forEach(audio => {
      if (!audio.seeking && Math.abs(audio.currentTime - time) > 0.045) audio.currentTime = time;
    });
    emit({ time: Math.max(0, Math.min(duration || time, time)) });
  };
  const maybeStart = () => {
    if (!wanted || state.playing || starting || disposed || !audios.length || audios.some(audio => audio.readyState < 3 || audio.seeking)) return;
    starting = true;
    const request = ++generation;
    Promise.all(audios.map(audio => audio.play())).then(() => {
      if (disposed || request !== generation || !wanted) {
        if (!wanted || disposed) pauseAudio();
        return;
      }
      starting = false;
      // Seeking the leader after every start can re-enter waiting/seek forever
      // for streamed WAV. Only followers with meaningful drift need correction.
      const leaderTime = audios[0].currentTime;
      audios.slice(1).forEach(audio => {
        if (!audio.seeking && Math.abs(audio.currentTime - leaderTime) > .045) audio.currentTime = leaderTime;
      });
      emit({ playing: true, buffering: false });
      clearInterval(timer); timer = setInterval(tick, 100);
    }).catch(() => { if (!disposed && request === generation) fail(); });
  };
  const ensureAudio = () => {
    if (audios.length) return;
    audios = STEM_NAMES.map(name => {
      const audio = audioFactory();
      // Elements are created only after a user action. Load enough data now so
      // the synchronized readiness barrier cannot stall behind preload=none.
      audio.preload = "auto";
      audio.src = urls[name];
      const listen = (event, handler) => {
        audio.addEventListener(event, handler);
        listeners.push(() => audio.removeEventListener(event, handler));
      };
      listen("loadedmetadata", () => { try { if (Math.abs(audio.currentTime - state.time) > .001) audio.currentTime = state.time; } catch (_) { /* Native media may still be initializing. */ } });
      listen("canplay", maybeStart);
      listen("seeked", maybeStart);
      listen("waiting", () => {
        if (!wanted || disposed || (!state.playing && !starting)) return;
        generation += 1; starting = false;
        clearInterval(timer); pauseAudio();
        const time = audios[0]?.currentTime || state.time;
        emit({ time, playing: false, buffering: true });
        audios.slice(1).forEach(follower => {
          if (!follower.seeking && Math.abs(follower.currentTime - time) > .045) follower.currentTime = time;
        });
      });
      listen("error", fail);
      listen("ended", () => { if (audio === audios[0]) { tick(); pause(); } });
      return audio;
    });
    applyMix(); align(state.time);
    audios.forEach(audio => audio.load());
  };
  const play = temporarySolo => {
    if (disposed) return;
    onBeforePlay();
    const time = duration > 0 && state.time >= duration - 0.02 ? 0 : state.time;
    emit({ temporarySolo: temporarySolo || null, time, error: "" });
    applyMix();
    if (state.playing) return;
    wanted = true;
    emit({ buffering: true }); ensureAudio(); align(time); maybeStart();
  };
  return {
    play,
    pause,
    seek(time) {
      const value = Math.max(0, Math.min(duration || 0, Number(time) || 0));
      emit({ time: value });
      if (wanted) {
        generation += 1; starting = false; clearInterval(timer); pauseAudio();
        emit({ playing: false, buffering: true });
      }
      align(value); maybeStart();
    },
    setMix(nextMix, nextMasterDb) { mix = nextMix; masterDb = nextMasterDb; applyMix(); },
    dispose() {
      disposed = true; pause(); listeners.forEach(remove => remove());
      audios.forEach(audio => { audio.removeAttribute("src"); audio.load(); }); audios = [];
    },
  };
}
